import type { TypeResolvedFeatures } from '../types';
import type { TypeLiveRequest, TypeUploadFeature } from '../core/types';

// ----------------------------------------------------------------------------

/*
 * Bounds a connection that goes quiet without ever closing.
 *
 * Uses the request hooks rather than `wrap`, because it has to see inside a request in flight:
 * the timer is re-armed on every upload progress event and only fires when none has arrived for
 * `timeoutMs`. A plain request timeout cannot do this — it would also cut off uploads that are
 * merely slow.
 *
 * Ending the request through `request.fail` rather than aborting it directly is what makes the
 * outcome a timeout instead of a cancellation, so the retry feature treats it as transient.
 */
function createStallFeature(features: TypeResolvedFeatures): TypeUploadFeature | null {
    const { enabled, timeoutMs } = features.stall;

    if (!enabled || timeoutMs <= 0) {
        return null;
    }

    // Per request: the same feature instance serves every request of the run.
    const timers = new WeakMap<XMLHttpRequest, number>();

    const fnClear = (request: TypeLiveRequest): void => {
        const timer = timers.get(request.xhr);

        if (timer !== undefined) {
            window.clearTimeout(timer);
            timers.delete(request.xhr);
        }
    };

    const fnArm = (request: TypeLiveRequest): void => {
        fnClear(request);

        timers.set(
            request.xhr,
            window.setTimeout(() => {
                timers.delete(request.xhr);
                request.fail('timeout', `Upload of "${request.label}" stalled for ${timeoutMs}ms`);
            }, timeoutMs),
        );
    };

    return {
        name: 'stall',
        request: {
            onStart: fnArm,
            onUploadProgress: (request, evt): void => {
                /*
                 * Once the body is fully sent no further progress event can arrive, so re-arming
                 * here would turn the stall timeout into a cap on the server's response time and
                 * abort — then retry — a request the server has already received.
                 */
                if (evt.total && evt.loaded >= evt.total) {
                    fnClear(request);
                    return;
                }

                fnArm(request);
            },
            onSettle: fnClear,
        },
    };
}

// ----------------------------------------------------------------------------

export default createStallFeature;
